<script setup>
import KitIcon from "@/components/approval-center/partials/kit/KitIcon.vue";
import FeedbackTooltip from "@/components/approval-center/modals/advanced-revision/FeedbackTooltip.vue";
import {computed, nextTick, onBeforeUnmount, onMounted, ref, watch} from "vue";
import MergeFeedbackWarningModal
  from "@/components/approval-center/modals/advanced-revision/MergeFeedbackWarningModal.vue";
import useAdvancedRevisionSelection from "@/composables/approval-center/advanced-revision/useAdvancedRevisionSelection";
import useAdvancedRevisionFeedback from "@/composables/approval-center/advanced-revision/useAdvancedRevisionFeedback";
import {useStore} from "vuex";
import BlogTourSteps from "@/components/tours/blog/BlogTourSteps.vue";
import AiBusinessProfileTourSteps from "@/components/tours/ai-business-profile/AiBusinessProfileTourSteps.vue";
import ToneOfVoiceTourSteps from "@/components/tours/tone-of-voice/ToneOfVoiceTourSteps.vue";

const props = defineProps({
  active: {
    type: Boolean,
    required: true,
  },
  content: {
    type: String,
    required: true,
  },
  preventScroll: {
    type: Boolean,
    required: true,
  }
});

const store = useStore();

const tourCurrentStep = computed(() => store.getters['tours/currentStep'])

const editableContent = ref(null);
const editableContentInner = ref(null);

const {
  activeFeedbackItem,
  closeFeedbackTooltip,
  updateFeedback,
  deleteFeedback,
  removeFeedbackItem,
  openFeedbackTooltip,
} = useAdvancedRevisionFeedback();

const {
  highlightSelection,
  limitSelectionToContentElement,
  showMergeWarningModal,
  mergeFeedbackItems,
  cancelMergingFeedbackItems,
} = useAdvancedRevisionSelection()

const exampleFeedbackItem = ref();

const makeExampleSelection = async (shouldOpenFeedbackTooltip = false) => {
  await nextTick();

  if (!shouldOpenFeedbackTooltip) {
    closeFeedbackTooltip();
  }

  const contentElement = editableContentInner.value;
  if (!contentElement) {
    return;
  }

  const selection = window.getSelection();
  if (!selection) {
    return;
  }

  selection.removeAllRanges();

  const textContent = contentElement.textContent ?? "";
  if (textContent.trim().length === 0) {
    selection.removeAllRanges();
    return;
  }

  const lineBreakPattern = /\r\n|\r|\n/g;
  const lines = [];
  let lastIndex = 0;
  let match;

  while ((match = lineBreakPattern.exec(textContent)) !== null) {
    lines.push({
      text: textContent.slice(lastIndex, match.index),
      start: lastIndex,
      end: match.index,
    });
    lastIndex = match.index + match[0].length;
  }

  lines.push({
    text: textContent.slice(lastIndex),
    start: lastIndex,
    end: textContent.length,
  });

  const meaningfulLines = lines.filter(line => line.text.trim().length > 0);

  if (!meaningfulLines.length) {
    return;
  }

  const targetLine = meaningfulLines.length >= 2 ? meaningfulLines[1] : meaningfulLines[0];

  const wordMatches = Array.from(targetLine.text.matchAll(/\S+/g));
  if (!wordMatches.length) {
    return;
  }

  const wordsToSelect = wordMatches.slice(0, Math.min(3, wordMatches.length));
  const firstWord = wordsToSelect[0];
  const lastWord = wordsToSelect[wordsToSelect.length - 1];
  if (!firstWord || !lastWord) {
    return;
  }

  const absoluteStart = targetLine.start + (firstWord.index ?? 0);
  const absoluteEnd = targetLine.start + (lastWord.index ?? 0) + lastWord[0].length;

  if (absoluteEnd <= absoluteStart) {
    return;
  }

  const range = document.createRange();
  const walker = document.createTreeWalker(contentElement, NodeFilter.SHOW_TEXT);

  let currentNode = walker.nextNode();
  let accumulated = 0;
  let rangeStartNode = null;
  let rangeStartOffset = 0;
  let rangeEndNode = null;
  let rangeEndOffset = 0;

  while (currentNode) {
    const nodeText = currentNode.textContent ?? "";
    const nodeStart = accumulated;
    const nodeEnd = accumulated + nodeText.length;

    if (!rangeStartNode && absoluteStart >= nodeStart && absoluteStart < nodeEnd) {
      rangeStartNode = currentNode;
      rangeStartOffset = absoluteStart - nodeStart;
    }

    if (!rangeEndNode && absoluteEnd >= nodeStart && absoluteEnd <= nodeEnd) {
      rangeEndNode = currentNode;
      rangeEndOffset = absoluteEnd - nodeStart;
      break;
    }

    accumulated = nodeEnd;
    currentNode = walker.nextNode();
  }

  if (rangeStartNode && rangeEndNode && editableContent.value) {
    range.setStart(rangeStartNode, rangeStartOffset);
    range.setEnd(rangeEndNode, rangeEndOffset);

    selection.removeAllRanges();
    selection.addRange(range);

    limitSelectionToContentElement(editableContent.value);

    setTimeout(async () => {
      exampleFeedbackItem.value = await highlightSelection(shouldOpenFeedbackTooltip);
    }, 10);
  }
};

watch(tourCurrentStep, function () {
  if (tourCurrentStep.value === 'select-text') {
    makeExampleSelection(false);
    return;
  }

  if (tourCurrentStep.value === 'add-comment') {
    openFeedbackTooltip(exampleFeedbackItem.value);
    return;
  }

  if (tourCurrentStep.value === 'edit-comment') {
    // We do this ensure the selection still shows after closing the feedback tooltip
    closeFeedbackTooltip();
    makeExampleSelection(false);

    return;
  }

  window.getSelection().removeAllRanges();
  removeFeedbackItem(exampleFeedbackItem.value);
  exampleFeedbackItem.value = undefined;
  closeFeedbackTooltip();
})

const handleSelectedRange = () => {
  const selection = window.getSelection();
  if (!selection || selection.toString().trim().length <= 0) {
    return;
  }

  // Check if the selection is within our content element
  if (!editableContent.value) {
    return;
  }

  // Check if the selection range intersects with our content element
  const range = selection.getRangeAt(0);
  if (!editableContent.value.contains(range.commonAncestorContainer)) {
    return;
  }

  limitSelectionToContentElement(editableContent.value);

  setTimeout(() => {
    highlightSelection();
  }, 10);
};

onMounted(() => {
  document.addEventListener('mouseup', handleSelectedRange);
});

onBeforeUnmount(() => {
  document.removeEventListener('mouseup', handleSelectedRange);
});
</script>

<template>
  <div class="tw-flex tw-justify-end">
    <div class="tw-px-4 tw-py-3 tw-bg-primary-100 tw-border-primary tw-border-t tw-border-x tw-rounded-t-lg">
      <div class="tw-text-neutral-900 tw-font-bold tw-h-2.5 tw-flex tw-items-center">
        Feedback Mode
      </div>
    </div>
  </div>
  <div
    class="tw-flex tw-border tw-rounded-lg tw-rounded-tr-none tw-gap-8 tw-p-8"
    :class="[active ? 'tw-border-primary' : 'tw-border-neutral']"
  >
    <KitIcon
      class="tw-self-start"
      state="thinking"
    />
    <div
      ref="editableContent"
      class="tw-max-h-[415px] tw-max-w-none"
      :class="[preventScroll ? 'tw-overflow-hidden' : 'tw-overflow-y-auto']"
    >
      <pre
        ref="editableContentInner"
        class="tw-text-base tw-bg-white tw-text-neutral-900 tw-p-0 tw-whitespace-pre-wrap"
        v-text="props.content"
      />
    </div>
  </div>

  <BlogTourSteps />
  <AiBusinessProfileTourSteps />
  <ToneOfVoiceTourSteps />

  <FeedbackTooltip
    v-if="activeFeedbackItem"
    :feedback-item="activeFeedbackItem"
    @update="updateFeedback"
    @delete="deleteFeedback"
    @cancel="closeFeedbackTooltip"
  />

  <MergeFeedbackWarningModal
    v-if="showMergeWarningModal"
    @merge="mergeFeedbackItems"
    @close="cancelMergingFeedbackItems"
  />
</template>
